import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SectionTitle } from './PageShell';
import { AdminGuard } from './AdminPages';
import AdminShell from './AdminShell';
import { api } from '../lib/api';
import { Users, FileText, BookOpenCheck, AlertTriangle, Clock, Loader2, ArrowRight, GraduationCap, UserCheck, RefreshCw, CheckCircle2, Inbox, Sparkles, PenSquare, ShieldCheck, MailOpen, Eye } from 'lucide-react';

const HUB_SECTIONS = [
  {
    to: '/admin/users',
    icon: Users,
    color: 'red',
    title: 'Utilisateurs',
    desc: 'Gérer les comptes élèves et enseignants : valider, désactiver, réinitialiser un mot de passe.',
    links: [
      { label: 'Élèves', to: '/admin/users?role=eleve' },
      { label: 'Enseignants', to: '/admin/users?role=enseignant' },
      { label: 'À valider', to: '/admin/users?status=pending' },
    ],
  },
  {
    to: '/admin/demandes',
    icon: Inbox,
    color: 'amber',
    title: 'Demandes & Réponses',
    desc: 'Boîte de réception du site : questions élèves, démos, partenariats. Répondre depuis contact@codevoiture.fr.',
    links: [
      { label: 'Nouvelles', to: '/admin/demandes' },
      { label: 'Historique des réponses', to: '/admin/demandes' },
    ],
  },
  {
    to: '/admin/articles',
    icon: FileText,
    color: 'emerald',
    title: 'Articles Magazine',
    desc: 'Créer, modifier, publier ou archiver les dossiers et enquêtes du magazine pédagogique.',
    links: [
      { label: 'Tous les articles', to: '/admin/articles' },
      { label: '+ Nouvel article', to: '/admin/articles/new' },
    ],
  },
  {
    to: '/admin/livret',
    icon: BookOpenCheck,
    color: 'sky',
    title: 'Suivi livret',
    desc: 'Consulter la progression des élèves sur les 38 compétences du livret d\'apprentissage.',
    links: [{ label: 'Voir les progressions', to: '/admin/livret' }],
  },
  {
    to: '/admin/actu',
    icon: Sparkles,
    color: 'fuchsia',
    title: 'Actu & Fun',
    desc: 'Gérer les actualités, contenus fun et animations visibles par les élèves sur le site.',
    links: [{ label: 'Gérer les contenus', to: '/admin/actu' }],
  },
  {
    to: '/enseignant/dashboard',
    icon: Eye,
    color: 'zinc',
    title: 'Côté enseignant & site public',
    desc: 'Basculer sur les outils pédagogiques ou vérifier ce que voient les visiteurs.',
    links: [
      { label: 'Dashboard enseignant', to: '/enseignant/dashboard' },
      { label: 'Page contact', to: '/contact' },
      { label: 'Magazine', to: '/magazine' },
    ],
  },
];

const HUB_COLORS = {
  red: 'border-red-700/40 hover:border-red-500/70 text-red-400 bg-red-600/15',
  amber: 'border-amber-700/40 hover:border-amber-500/70 text-amber-400 bg-amber-600/15',
  emerald: 'border-emerald-700/40 hover:border-emerald-500/70 text-emerald-400 bg-emerald-600/15',
  sky: 'border-sky-700/40 hover:border-sky-500/70 text-sky-400 bg-sky-600/15',
  fuchsia: 'border-fuchsia-700/40 hover:border-fuchsia-500/70 text-fuchsia-400 bg-fuchsia-600/15',
  zinc: 'border-white/15 hover:border-white/40 text-white/70 bg-white/10',
};

function HubCard({ section, badge }) {
  const Icon = section.icon;
  const colors = HUB_COLORS[section.color];
  const [borderCls, hoverCls, iconText, iconBg] = colors.split(' ');
  return (
    <div className={`rounded-2xl border ${borderCls} ${hoverCls} bg-[#0d0d0e] p-5 flex flex-col transition group`} data-testid={`hub-${section.to.replace(/\//g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl border ${borderCls} ${iconBg} ${iconText} flex items-center justify-center`}>
          <Icon size={19} />
        </div>
        {badge > 0 && (
          <span className="px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] font-bold" data-testid={`hub-badge-${section.color}`}>{badge}</span>
        )}
      </div>
      <Link to={section.to} className="font-serif-editorial text-white text-[20px] leading-tight hover:text-red-300 transition">{section.title}</Link>
      <p className="text-white/55 text-[12.5px] leading-relaxed mt-1.5 flex-1">{section.desc}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {section.links.map((l, i) => (
          <Link key={i} to={l.to} className={`inline-flex items-center gap-1 px-3 h-8 rounded-full border border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/10 text-[12px] font-semibold transition`}>
            {l.label} <ArrowRight size={10} />
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent = 'red', testid }) {
  const accentClass = {
    red: 'text-red-400 bg-red-600/15 border-red-700/40',
    amber: 'text-amber-400 bg-amber-600/15 border-amber-700/40',
    emerald: 'text-emerald-400 bg-emerald-600/15 border-emerald-700/40',
    sky: 'text-sky-400 bg-sky-600/15 border-sky-700/40',
  }[accent];
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6 hover:border-red-700/40 transition">
      <div className="flex items-start justify-between mb-4">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec">{label}</div>
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${accentClass}`}>
          <Icon size={16} />
        </div>
      </div>
      <div className="font-serif-editorial text-white text-[40px] lg:text-[48px] leading-none">{value}</div>
      {sub && <div className="text-white/50 text-[12.5px] mt-2">{sub}</div>}
    </div>
  );
}

function ts(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return d; }
}

const ROLE_LABEL = { eleve: 'Élève', enseignant: 'Enseignant' };
const ROLE_ICON = { eleve: GraduationCap, enseignant: UserCheck };

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    Promise.all([api.adminStats(), api.adminActivity(15)])
      .then(([s, a]) => { setStats(s); setActivity(a); })
      .catch(() => { /* admin guard will render Accès refusé for non-admin */ })
      .finally(() => setLoading(false));
  }, []);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.adminSyncArticles();
      setSyncResult({ ok: true, ...res });
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.detail || 'Erreur inconnue' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <AdminGuard>
      <AdminShell>
        <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10" data-testid="admin-dashboard">
          <SectionTitle
            kicker="Back-office · Contrôle, vérification, validation"
            title="Tableau de bord administrateur"
            sub="Toute la plateforme au même endroit : utilisateurs, demandes, contenus, progressions."
          />

          {/* 📌 À traiter aujourd'hui — priorités du jour en premier */}
          {stats && ((stats.demandes?.new || 0) > 0 || stats.users.pending > 0) && (
            <div className="mb-8 rounded-2xl border border-amber-600/40 bg-amber-950/15 p-4 sm:p-5" data-testid="admin-today-todo">
              <div className="text-amber-300 text-[11px] uppercase tracking-[0.2em] font-bold font-mono-spec mb-3">📌 À traiter aujourd&apos;hui</div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
                {(stats.demandes?.new || 0) > 0 && (
                  <Link to="/admin/demandes" data-testid="todo-demandes" className="flex items-center gap-3 rounded-xl border border-sky-700/40 bg-sky-950/25 hover:bg-sky-950/40 px-4 py-3 transition flex-1 min-w-[220px]">
                    <MailOpen className="text-sky-400 shrink-0" size={18} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sky-200 text-[13.5px] font-semibold">{stats.demandes.new} demande{stats.demandes.new > 1 ? 's' : ''} de contact</div>
                      <div className="text-white/50 text-[11.5px]">Questions élèves, démos, partenariats</div>
                    </div>
                    <ArrowRight size={14} className="text-sky-400 shrink-0" />
                  </Link>
                )}
                {stats.users.pending > 0 && (
                  <Link to="/admin/users?status=pending" data-testid="todo-pending" className="flex items-center gap-3 rounded-xl border border-amber-700/40 bg-amber-950/25 hover:bg-amber-950/40 px-4 py-3 transition flex-1 min-w-[220px]">
                    <AlertTriangle className="text-amber-400 shrink-0" size={18} />
                    <div className="flex-1 min-w-0">
                      <div className="text-amber-200 text-[13.5px] font-semibold">{stats.users.pending} compte{stats.users.pending > 1 ? 's' : ''} à valider</div>
                      <div className="text-white/50 text-[11.5px]">Enseignants en attente d&apos;approbation</div>
                    </div>
                    <ArrowRight size={14} className="text-amber-400 shrink-0" />
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Hub d'accès à toutes les zones du back-office */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="admin-hub">
            {HUB_SECTIONS.map((s) => (
              <HubCard
                key={s.to}
                section={s}
                badge={s.to === '/admin/demandes' ? (stats?.demandes?.new || 0) : s.to === '/admin/users' ? (stats?.users?.pending || 0) : 0}
              />
            ))}
          </div>

          {loading || !stats ? (
            <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
          ) : (
            <>
              {/* Stats grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard testid="stat-users-total" label="Utilisateurs" value={stats.users.total} sub={`${stats.users.eleves} élèves · ${stats.users.enseignants} enseignants`} icon={Users} />
                <StatCard testid="stat-users-pending" label="En attente" value={stats.users.pending} sub={`${stats.users.disabled} comptes désactivés`} icon={AlertTriangle} accent="amber" />
                <StatCard testid="stat-articles-published" label="Articles publiés" value={stats.articles.published} sub={`${stats.articles.total} total · ${stats.articles.archived} archivés`} icon={FileText} accent="emerald" />
                <StatCard testid="stat-livret-done" label="Compétences validées" value={stats.livret.entries_done} sub={`sur ${stats.livret.entries_total} entrées livret`} icon={BookOpenCheck} accent="sky" />
              </div>

              {/* Nouvelles demandes banner */}
              {(stats.demandes?.new || 0) > 0 && (
                <div className="mt-6 rounded-2xl border border-sky-700/40 bg-sky-950/20 p-5 flex items-start gap-4" data-testid="demandes-banner">
                  <MailOpen className="text-sky-400 shrink-0" size={22} />
                  <div className="flex-1">
                    <div className="text-sky-300 font-semibold text-[14.5px]">{stats.demandes.new} demande{stats.demandes.new > 1 ? 's' : ''} de contact à traiter</div>
                    <div className="text-white/60 text-[13px] mt-1">Questions d&apos;élèves, démos ou partenariats en attente de ta réponse.</div>
                  </div>
                  <Link to="/admin/demandes" className="h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-sky-950 text-[13px] font-bold inline-flex items-center gap-1.5 shrink-0">
                    Répondre <ArrowRight size={13} />
                  </Link>
                </div>
              )}

              {/* Pending banner */}
              {stats.users.pending > 0 && (
                <div className="mt-6 rounded-2xl border border-amber-700/40 bg-amber-950/20 p-5 flex items-start gap-4" data-testid="pending-banner">
                  <AlertTriangle className="text-amber-400 shrink-0" size={22} />
                  <div className="flex-1">
                    <div className="text-amber-300 font-semibold text-[14.5px]">{stats.users.pending} compte{stats.users.pending > 1 ? 's' : ''} en attente de validation</div>
                    <div className="text-white/60 text-[13px] mt-1">Des enseignants ou futurs enseignants attendent ton approbation pour accéder à leur espace pro.</div>
                  </div>
                  <Link to="/admin/users?status=pending" className="h-10 px-4 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 text-[13px] font-bold inline-flex items-center gap-1.5 shrink-0">
                    Valider <ArrowRight size={13} />
                  </Link>
                </div>
              )}

              {/* Sync articles Preview → Production */}
              <div className="mt-6 rounded-2xl border border-sky-700/40 bg-sky-950/15 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="sync-articles-panel">
                <div className="w-11 h-11 rounded-xl bg-sky-600/20 border border-sky-700/40 flex items-center justify-center text-sky-300 shrink-0">
                  <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
                </div>
                <div className="flex-1">
                  <div className="text-sky-200 font-semibold text-[14.5px]">Synchroniser les articles Magazine (Preview → Production)</div>
                  <div className="text-white/60 text-[13px] mt-1">
                    Recopie le dernier snapshot d&apos;articles (couvertures, contenus, nouveaux numéros) dans la base MongoDB de cet environnement.
                    À utiliser après chaque nouveau déploiement pour voir les mises à jour en prod.
                  </div>
                  {syncResult && syncResult.ok && (
                    <div className="mt-3 inline-flex items-center gap-2 text-emerald-300 text-[13px] font-semibold">
                      <CheckCircle2 size={14} />
                      Sync OK · {syncResult.created} créés · {syncResult.updated} mis à jour · {syncResult.unchanged} inchangés (total {syncResult.total})
                    </div>
                  )}
                  {syncResult && !syncResult.ok && (
                    <div className="mt-3 text-red-300 text-[13px] font-semibold">Erreur : {syncResult.error}</div>
                  )}
                </div>
                <button
                  data-testid="sync-articles-btn"
                  onClick={runSync}
                  disabled={syncing}
                  className="h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 disabled:opacity-60 disabled:cursor-not-allowed text-sky-950 text-[13px] font-bold inline-flex items-center gap-1.5 shrink-0"
                >
                  {syncing ? (<><Loader2 size={13} className="animate-spin" /> Sync…</>) : (<><RefreshCw size={13} /> Lancer la sync</>)}
                </button>
              </div>

              {/* Activity */}
              <div className="grid lg:grid-cols-2 gap-6 mt-10">
                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif-editorial text-white text-[22px]">Dernières inscriptions</h3>
                    <Link to="/admin/users" className="text-[12px] text-red-400 hover:text-red-300 font-semibold inline-flex items-center gap-1">Tous <ArrowRight size={11} /></Link>
                  </div>
                  <ul className="divide-y divide-white/5">
                    {(activity?.recent_users || []).slice(0, 8).map((u) => {
                      const Icon = ROLE_ICON[u.role] || Users;
                      return (
                        <li key={u.id} className="py-3 flex items-center gap-3" data-testid={`activity-user-${u.id}`}>
                          <div className="w-9 h-9 rounded-lg bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0">
                            <Icon size={14} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-[13.5px] font-semibold truncate">{u.name}</div>
                            <div className="text-white/45 text-[11.5px] truncate font-mono-spec">{u.email}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10.5px] uppercase tracking-wider text-white/50 font-mono-spec">{ROLE_LABEL[u.role] || u.role}</div>
                            <div className="text-[11px] text-white/35 mt-0.5 inline-flex items-center gap-1"><Clock size={10} />{ts(u.created_at)}</div>
                          </div>
                          {u.status === 'pending' && <span className="ml-2 px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 text-[10px] font-bold uppercase tracking-wider">À valider</span>}
                          {u.status === 'disabled' && <span className="ml-2 px-2 py-0.5 rounded-md bg-red-500/20 text-red-300 text-[10px] font-bold uppercase tracking-wider">Désactivé</span>}
                        </li>
                      );
                    })}
                    {(!activity?.recent_users || activity.recent_users.length === 0) && (
                      <li className="py-6 text-center text-white/40 text-[13px]">Aucune inscription récente.</li>
                    )}
                  </ul>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif-editorial text-white text-[22px]">Articles récemment modifiés</h3>
                    <Link to="/admin/articles" className="text-[12px] text-red-400 hover:text-red-300 font-semibold inline-flex items-center gap-1">Tous <ArrowRight size={11} /></Link>
                  </div>
                  <ul className="divide-y divide-white/5">
                    {(activity?.recent_articles || []).slice(0, 8).map((a) => (
                      <li key={a.id} className="py-3 flex items-center gap-3" data-testid={`activity-article-${a.id}`}>
                        <div className="w-9 h-9 rounded-lg bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0">
                          <FileText size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[13.5px] font-semibold truncate">{a.title}</div>
                          <div className="text-white/45 text-[11.5px] truncate font-mono-spec">{a.slug}</div>
                        </div>
                        <div className="text-right">
                          {a.archived ? <span className="text-[10.5px] uppercase tracking-wider text-white/40 font-mono-spec">archivé</span>
                            : a.published ? <span className="text-[10.5px] uppercase tracking-wider text-emerald-400 font-mono-spec">publié</span>
                            : <span className="text-[10.5px] uppercase tracking-wider text-white/45 font-mono-spec">brouillon</span>}
                          <div className="text-[11px] text-white/35 mt-0.5 inline-flex items-center gap-1"><Clock size={10} />{ts(a.updated_at)}</div>
                        </div>
                      </li>
                    ))}
                    {(!activity?.recent_articles || activity.recent_articles.length === 0) && (
                      <li className="py-6 text-center text-white/40 text-[13px]">Aucun article modifié récemment.</li>
                    )}
                  </ul>
                </div>
              </div>
            </>
          )}
        </section>
      </AdminShell>
    </AdminGuard>
  );
}
